<template>
  <ui-modal-layout :title="stepTitle" :icon="stepIcon">
    <div v-if="step === 1" key="step-1" class="flex flex-col gap-5 animate-in fade-in slide-in-from-right-4 duration-300">
      <p class="m-0 text-muted-foreground">
        {{ tr('Welcome to the :app platform! As your first step, please connect your Meta Business account to our system. Add your Facebook pages, Instagram profiles, and ad accounts to fully leverage all the benefits of our AI-powered automated ad management solution. As an official Meta Tech Provider, we guarantee that your data will be handled securely in accordance with Meta\'s strict regulations.') }}
      </p>

      <div class="relative mt-10 flex h-36 items-end justify-center gap-24">
        <lottie-animation
          class="absolute top-0 left-1/2 w-50 -translate-x-[calc(50%-16px)] -translate-y-12 [transform:rotateX(180deg)_rotate(-18deg)]"
          :animation-data="connectArrowAnimation"
          :auto-play="true"
          :loop="true"
        />
        <i class="icon facebook-circle-filled primary relative top-1 size-19.5" />
        <img :src="appLogo" alt="" class="size-16 min-w-16 object-contain">
      </div>
    </div>

    <div v-else-if="step === 2" key="step-2" class="flex flex-col gap-5 animate-in fade-in slide-in-from-right-4 duration-300">
      <p class="m-0 text-muted-foreground">
        {{ tr('In the next step, select the ad accounts where you want to run your ads.') }}
      </p>
      <ad-account-select-input v-model="selectedAdAccountExternalId" />
    </div>

    <div v-else-if="step === 3" key="step-3" class="flex flex-col gap-5 animate-in fade-in slide-in-from-right-4 duration-300">
      <p class="m-0 text-muted-foreground" v-html="tr('In the next step, select the Facebook pages and Instagram profiles you want to use for advertising.')" />
      <facebook-page-select-input
        v-model="selectedFacebookPageExternalId"
        :disabled="isBrandCreating"
        :loading="isBrandCreating"
      />
    </div>

    <div v-else-if="step === 4" key="step-4" class="flex flex-col gap-5 animate-in fade-in slide-in-from-right-4 duration-300">
      <p class="m-0 text-muted-foreground" v-html="tr('In this step, we\'ll start setting up your first AI-powered automation. Choose the advertising goal you want to achieve, and we\'ll prepare the setup for you.')" />
      <div class="flex flex-col gap-2">
        <button
          v-for="option in goalOptions"
          :key="option.value"
          type="button"
          data-slot="onboarding-goal"
          :class="[ 'glass-tile flex w-full cursor-pointer items-center gap-3 rounded-xl p-3 text-left hover:[--tile-glow:color-mix(in_oklab,var(--primary)_40%,transparent)]', GLOW_TRANSITION ]"
          @click="() => selectGoal(option.value)"
        >
          <i :class="[ 'icon primary size-9 shrink-0', option.icon ]" />
          <span class="flex min-w-0 flex-1 flex-col">
            <span class="text-sm font-semibold text-foreground">{{ tr(option.title) }}</span>
            <span class="text-xs text-muted-foreground">{{ tr(option.description) }}</span>
          </span>
          <chevron-right-icon class="size-5 shrink-0 text-primary" />
        </button>
      </div>
    </div>

    <template #footer>
      <div class="flex w-full flex-col items-center gap-4">
        <ui-button
          v-if="step === 1"
          class="w-full"
          :disabled="isConnecting"
          @click="() => connect()"
        >
          <loader-circle-icon v-if="isConnecting" class="animate-spin" />
          {{ tr('Connect your Facebook profile') }}
        </ui-button>

        <div class="flex justify-center gap-2.5" aria-hidden="true">
          <span
            v-for="dot in steps"
            :key="'onboarding-dot-' + dot"
            :class="[ 'h-1.5 rounded-full transition-all duration-300', step === dot ? 'w-7 bg-primary' : 'w-4 bg-primary/25' ]"
          />
        </div>

        <img class="w-19" src="~@/assets/img/onboarding/meta-badge.png" alt="Meta Business Partner">
      </div>
    </template>
  </ui-modal-layout>
</template>

<script>
import { ChevronRight, Flag, LoaderCircle, Plug, Target, Wallet } from '@lucide/vue';
import { LottieAnimation } from 'lottie-web-vue';
import { markRaw } from 'vue';

import mainLogo from '@/assets/img/icons/main-logo.svg';
import mtuLogoCrest from '@/assets/img/mtu-logo-crest.svg';
import { GOAL_OPTIONS } from '@/consts';
import ConnectFacebookProfileMixin from '@/mixins/ConnectFacebookProfile';
import BrandService from '@/services/Brand.js';
import AdAccountSelectInput from '@/ui/components/AdAccountSelectInput.vue';
import FacebookPageSelectInput from '@/ui/components/FacebookPageSelectInput.vue';
import HasNoAssetsModalContent from '@/ui/components/HasNoAssetsModalContent.vue';
import { GLOW_TRANSITION } from '@/ui/utils/ruleAction.js';

const STEP_ICONS = {
  1: markRaw(Plug),
  2: markRaw(Wallet),
  3: markRaw(Flag),
  4: markRaw(Target),
};

export default {
  name: 'OnboardingAd',
  components: {
    AdAccountSelectInput,
    ChevronRightIcon: ChevronRight,
    FacebookPageSelectInput,
    LoaderCircleIcon: LoaderCircle,
    LottieAnimation,
  },
  mixins: [ ConnectFacebookProfileMixin ],
  data: () => ({
    GLOW_TRANSITION,
    step: 1,
    isBrandCreating: false,
    selectedAdAccountExternalId: null,
    selectedFacebookPageExternalId: null,
    goalOptions: GOAL_OPTIONS,
    appLogo: process.env.VUE_APP_FORK === 'turisztikai' ? mtuLogoCrest : mainLogo,
  }),
  computed: {
    steps() {
      return this.skipFacebookProfile ? [ 2, 3, 4 ] : [ 1, 2, 3, 4 ];
    },
    stepIcon() {
      return STEP_ICONS[this.step];
    },
    stepTitle() {
      switch (this.step) {
        case 1:
          return this.tr('Connect Facebook Profile');

        case 2:
          return this.tr('Select Ad account');

        case 3:
          return this.tr('Select Facebook Pages and Instagram profiles');

        default:
          return this.tr('What is the goal of your first automation?');
      }
    },
    skipFacebookProfile() {
      return this.$root.currentCompany.connected_profiles.length !== 0;
    },
    connectArrowAnimation() {
      if (this.$app.isShoprenterApp) {
        return require('@/assets/lottie/connect-arrow--shoprenter.json');
      }

      return require('@/assets/lottie/connect-arrow.json');
    },
    selectedFacebookPageName() {
      const externalId = this.selectedFacebookPageExternalId;
      if (!externalId) {
        return '';
      }

      const sp = this.$root.currentCompany.source_profiles.find(p => p.platform === 'facebook' && p.external_id === externalId);
      if (sp) {
        return sp.name;
      }

      const connectedProfiles = this.$root.loggedInUser.connected_profiles.filter(p => p.platform === 'facebook');
      for (const profile of connectedProfiles) {
        const page = (profile.details?.pages || []).find(p => p.id === externalId);
        if (page) {
          return page.name;
        }
      }

      return '';
    },
  },
  watch: {
    selectedAdAccountExternalId(val) {
      if (val) {
        ++this.step;
      }
    },
    selectedFacebookPageExternalId(val) {
      if (val) {
        this.isBrandCreating = true;

        BrandService.create({
          name: this.selectedFacebookPageName,
          meta_ad_account_external_id: this.selectedAdAccountExternalId,
          facebook_page_external_id: val,
        })
          .then(resp => {
            this.$store.dispatch('auth/fetch');

            ++this.step;

            return resp;
          })
          .catch(err => {
            this.error(err);
            this.selectedFacebookPageExternalId = null;
          })
          .finally(() => this.isBrandCreating = false);
      }
    },
  },
  created() {
    if (this.skipFacebookProfile && this.step === 1) {
      this.step = 2;
    }
  },
  methods: {
    onFacebookProfileConnected() {
      if (this.$root.loggedInUser.connected_profiles[0].details.ad_accounts.length === 0) {
        this.$emit('close');
        this.$fullscreenModal.open(
          this,
          HasNoAssetsModalContent,
          { asset: 'ad-account' },
        );
        return;
      }
      if (
        this.$root.loggedInUser.connected_profiles[0].details.pages.length === 0
        && this.$root.loggedInUser.connected_profiles[0].details.instagram_accounts.length === 0
      ) {
        this.$emit('close');
        this.$fullscreenModal.open(
          this,
          HasNoAssetsModalContent,
          { asset: 'source-profile' },
        );
        return;
      }
      ++this.step;
    },
    selectGoal(goal) {
      this.$toast.clear();
      this.$emit('close');

      this.$router.push({
        path: '/automation/new/ai-mode',
        query: {
          goal,
          guided: 1,
        },
        hash: '#step-1',
      });
    },
  },
};
</script>
